import queryString from 'query-string'
import { put, takeLatest, call } from 'redux-saga/effects'

import { IdentityProviderType } from '@b-stock/account-api-client'
import { ddLogger } from '@b-stock/fe-core/bstock-react'

import signUpApi from '@api/signUp'
import appConfig from '@config/config'
import type { OAuthChallenge } from '@helpers/auth/oauthChallenge'
import {
  challengeParams,
  clearOAuthChallenge,
  clearReauthorizeAttempted,
  isWebCryptoAvailable,
  mintOAuthChallenge,
  storeOAuthChallenge,
  withCsrf,
} from '@helpers/auth/oauthChallenge'
import { sanitizeEmail } from '@helpers/sanitize'
import { assign, getOrigin } from '@helpers/windowLocation'

import signupTypes from '../signUp/types'
import type { SubmitEmailValues } from './types'
import types from './types'

const { api, baseUrl, loginTermsPolicyKey } = appConfig

const {
  oauth2,
  salesforce,
  auth: { clientId },
} = api

const { checkUserExists } = signUpApi

export function* submitEmailSaga({
  values: { email, ...rest },
}: {
  type: string
  values: SubmitEmailValues
}): Generator {
  const sanitizedEmail = sanitizeEmail(email)
  try {
    yield put({ type: types.SIGN_IN_SUBMIT_EMAIL_PENDING })

    // Minted once up-front so whichever authorize branch runs below shares
    // the same pair.
    let challenge: OAuthChallenge | null = null
    if (isWebCryptoAvailable()) {
      challenge = (yield call(mintOAuthChallenge)) as OAuthChallenge
      // A fresh user-initiated login gets a fresh retry budget.
      clearReauthorizeAttempted()
      if (!storeOAuthChallenge(challenge)) {
        ddLogger.warn(
          'Could not persist the OAuth challenge; the callback will find no pair and must re-authorize'
        )
        challenge = null
      }
    } else {
      // Sizes the population the callback locks out: it refuses a
      // verifier-less exchange. `secureContext` separates an insecure origin
      // from a browser that genuinely lacks SubtleCrypto.
      ddLogger.info('login pkce unavailable', {
        reason: 'webcrypto-unavailable',
        identityProviderType: rest.identityProviderType,
        isEnterpriseUser: rest.isEnterpriseUser,
        secureContext:
          typeof window === 'undefined' ? undefined : window.isSecureContext,
      })
    }

    // if user is from magento — but the 3MP identity provider is authoritative,
    // so a Salesforce person must not be routed here on the strength of a
    // legacy UA record alone.
    const { isEnterpriseUser, identityProviderType: restIdentityProviderType } =
      rest
    if (
      isEnterpriseUser &&
      restIdentityProviderType !== IdentityProviderType.Salesforce
    ) {
      // `tc` tells /login to record terms acceptance. Both FusionAuth-bound
      // branches send it.
      const params = queryString.stringify({
        client_id: clientId,
        redirect_uri:
          typeof document === 'undefined'
            ? 'Unable to determine redirect uri!'
            : `${getOrigin()}${baseUrl}/login`,
        response_type: 'code',
        scope: 'offline_access',
        email: sanitizedEmail,
        state: JSON.stringify(
          withCsrf({ ...rest, tc: loginTermsPolicyKey }, challenge)
        ),
        ...challengeParams(challenge),
      })
      assign(`${oauth2.authorize}?${params}`)
      return
    }
    const {
      hasCredentials,
      isEmailVerified,
      identityProviderType,
      identityProviderUserId,
    } = (yield call(checkUserExists, {
      email: sanitizedEmail,
    })) as Awaited<ReturnType<typeof checkUserExists>>

    // If the user has created an account and login credentials, OR BE
    // identified them as a possible salesforce and are elegible for
    // auto-provisioning, redirect to the relevant identity provider.
    if (
      (isEmailVerified && hasCredentials) ||
      identityProviderType === IdentityProviderType.Salesforce
    ) {
      yield put({
        type: types.SIGN_IN_SUBMIT_EMAIL_FULFILLED,
        email: sanitizedEmail,
      })

      // Note: I'm pretty sure this is dead code, but out of an abundance of
      // caution, I'm leaving it in until we can prove it's not used by
      // anything. - Ryan, 2024-03-12
      const r = rest as Record<string, unknown>
      if (r.password || r.verificationCode) {
        ddLogger.error(
          "A password or verification code was submitted to the sign in saga. This indicates an error in the saga's caller."
        )
        delete r.password
        delete r.verificationCode
      }

      // send the user to the appropriate identity provider
      if (identityProviderType === IdentityProviderType.Fusionauth) {
        const params = queryString.stringify({
          client_id: clientId,
          redirect_uri:
            typeof document === 'undefined'
              ? 'Unable to determine redirect uri!'
              : `${getOrigin()}${baseUrl}/login`,
          response_type: 'code',
          scope: 'offline_access',
          email: sanitizedEmail,
          state: JSON.stringify(
            withCsrf(
              { role: 'buyer', ...rest, tc: loginTermsPolicyKey },
              challenge
            )
          ),
          ...challengeParams(challenge),
        })
        assign(`${oauth2.authorize}?${params}`)
        return
      } else if (identityProviderType === IdentityProviderType.Salesforce) {
        // No `tc`: Salesforce's login page doesn't show the consent notice.
        const params = queryString.stringify({
          client_id: salesforce.clientId,
          redirect_uri:
            typeof document === 'undefined'
              ? 'Unable to determine redirect uri!'
              : `${getOrigin()}${baseUrl}/login/salesforce`,
          response_type: 'code',
          state: JSON.stringify(withCsrf(rest, challenge)),
          ...(identityProviderUserId
            ? { login_hint: identityProviderUserId }
            : {}),
          ...challengeParams(challenge),
        })
        assign(`${salesforce.authorize}?${params}`)
        return
      } else {
        throw new Error(
          `Unexpected identityProviderType: ${identityProviderType}`
        )
      }
    } else if (isEmailVerified && !hasCredentials) {
      yield put({
        type: signupTypes.CHECK_EMAIL_FULFILLED,
        payload: { email: sanitizedEmail, isEmailVerified, hasCredentials },
      })
    } else {
      yield put({
        type: types.SIGN_IN_SUBMIT_EMAIL_REJECTED,
        email: sanitizedEmail,
        error: true,
      })
    }

    // No authorize redirect happened, so a left-behind pair would make a
    // later code arriving in this tab read as a csrf mismatch.
    clearOAuthChallenge()
  } catch (error) {
    clearOAuthChallenge()
    yield put({
      type: types.SIGN_IN_SUBMIT_EMAIL_REJECTED,
      error,
    })
    console.error(error)
  }
}

export default function* signinSaga() {
  try {
    yield takeLatest(types.SIGN_IN_SUBMIT_EMAIL, submitEmailSaga)
  } catch (error) {
    ddLogger.error(
      'SignIn error',
      {},
      error instanceof Error
        ? error
        : new Error('Original error did not have a stack trace')
    )
  }
}
